import { useEffect, useMemo, useState } from 'react';

import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';
import { canRole } from 'security/rbac';
import { subscribeToWorkspaceRole } from 'services/workspaces/WorkspaceRoleService';

export default function useWorkspaceAccess(workspaceOverride = null) {
  const { user } = useUser();
  const { activeWorkspace } = useWorkspace();

  const effectiveWorkspace = workspaceOverride || activeWorkspace || null;

  const [workspaceRole, setWorkspaceRole] = useState(null);
  const [isRoleLoading, setIsRoleLoading] = useState(true);

  const workspaceKey = effectiveWorkspace?.key || null;
  const userKey = user?.key || null;

  useEffect(() => {
    if (!workspaceKey || !userKey) {
      setWorkspaceRole(null);
      setIsRoleLoading(false);
      return;
    }

    if (effectiveWorkspace?.ownerKey === userKey) {
      setWorkspaceRole('owner');
      setIsRoleLoading(false);
      return;
    }

    setIsRoleLoading(true);

    const unsubscribe = subscribeToWorkspaceRole({
      workspaceKey,
      userKey,
      workspace: effectiveWorkspace,
      callback: (role) => {
        setWorkspaceRole(role || null);
        setIsRoleLoading(false);
      },
      onError: () => {
        setWorkspaceRole(null);
        setIsRoleLoading(false);
      },
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [effectiveWorkspace, userKey, workspaceKey]);

  return useMemo(() => ({
    workspaceRole,
    isRoleLoading,
    can: (action) => canRole(workspaceRole, action),
  }), [isRoleLoading, workspaceRole]);
}
